import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Clock, Video, MessagesSquare, ArrowRight } from "lucide-react";
import { PageHeader, SectionHeading } from "../components/PageBits";
import { Reveal } from "../components/Reveal";

const FORMATO = [
  { icon: Calendar, t: "Martes y jueves." },
  { icon: Clock, t: "9:00 PM (EST)." },
  { icon: Video, t: "En vivo por Zoom." },
  { icon: MessagesSquare, t: "Espacio abierto para preguntas, reflexión y acompañamiento." },
];

export default function ProgramsPage() {
  return (
    <>
      {/* 1. Hero */}
      <PageHeader
        overline="Acompañamiento en vivo"
        titleLines={["Puedes recorrer", "este camino solo."]}
        accentLines={["O puedes vivirlo", "acompañado."]}
        subtitle="Hay una diferencia entre comprender algo y sostenerlo en el tiempo. El contenido te ofrece un mapa. El acompañamiento te ayuda a recorrerlo cuando aparecen las dudas, los bloqueos o los momentos en los que lo aprendido necesita convertirse en experiencia."
      />

      {/* 2. Qué significa acompañamiento */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="programs-meaning">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="No es otra clase"
            title="Comprender juntos"
            accent="transforma la experiencia."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Cuando recorres un proceso completamente solo, muchas preguntas quedan sin hacerse y
                  las dudas se acumulan hasta apagar el entusiasmo inicial.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Cuando compartes ese mismo proceso con otras personas, las preguntas encuentran
                  respuesta mientras todavía están vivas.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                Este espacio no existe para repetir el contenido.
                <br />
                <span className="italic text-gold-deep">Existe para ayudarte a integrarlo.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. Cómo se siente un encuentro */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="programs-encounter">
        <div className="container-luxe relative z-10">
          <SectionHeading
            overline="Lo que sucede cuando nos reunimos"
            title="Un espacio donde puedes llegar"
            accent="exactamente como estás."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  Puedes llegar con una duda, una reflexión, una emoción difícil o simplemente con la
                  necesidad de escuchar.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  No necesitas tener respuestas. Aquí todos recorremos el mismo camino desde lugares
                  diferentes.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-cream">
                No hay espectadores.
                <br />
                <span className="italic text-gold">Hay personas aprendiendo juntas.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. Ritmo y formato */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="programs-format">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Ritmo del acompañamiento"
            title="Dos encuentros por semana para"
            accent="sostener tu proceso."
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {FORMATO.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08}>
                <div
                  className="flex items-center gap-5 rounded-sm border border-navy/10 bg-white/60 p-6"
                  data-testid={`programs-format-${i}`}
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold-deep/40 text-gold-deep">
                    <f.icon size={20} />
                  </span>
                  <p className="text-base font-light leading-relaxed text-navy/80">{f.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Para quién es este espacio */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="programs-audience">
        <div className="container-luxe relative z-10">
          <SectionHeading
            overline="¿Para quién es?"
            title="El complemento natural de"
            accent="tu recorrido."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <p className="text-lg font-light leading-relaxed text-cream/75">
                Este espacio está pensado para quienes desean sostener su proceso junto a una
                comunidad y profundizar en lo que van integrando.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-cream">
                No reemplaza el recorrido personal.
                <br />
                <span className="italic text-gold">Lo fortalece.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="programs-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              El conocimiento transforma la manera de pensar.
              <br />
              <span className="italic text-gold">El acompañamiento ayuda a transformar la manera de vivir.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col items-start gap-6">
              <Link to="/contact" className="btn-gold" data-testid="programs-cta">
                Unirme al próximo encuentro <ArrowRight size={15} />
              </Link>
              <Link
                to="/library"
                className="group inline-flex items-center gap-2 text-sm text-cream/70 underline-offset-4 transition-colors hover:text-gold hover:underline"
                data-testid="programs-library-link"
              >
                Aún no formas parte de la Biblioteca Premium
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
